import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cx } from "@/components/ui";
import { MathText } from "@/components/math-text";

/** Render a string with LaTeX, or pass non-string children through. */
export function Markup({ children }: { children: ReactNode }) {
  return typeof children === "string" ? <MathText text={children} /> : <>{children}</>;
}

/** Prompt heading shared by all renderers — preserves authored line breaks and
 *  renders LaTeX when the child is a string. */
export function Prompt({ children }: { children: ReactNode }) {
  return (
    <div className="whitespace-pre-wrap text-lg font-semibold leading-snug text-ink">
      <Markup>{children}</Markup>
    </div>
  );
}

/** Post-reveal explanation block (renders LaTeX). */
export function Explanation({ text }: { text?: string }) {
  const { t } = useTranslation();
  if (!text) return null;
  return (
    <div className="mt-4 rounded-xl bg-brand-50 p-3.5 text-sm text-brand-900/80">
      <span className="font-semibold text-brand-700">{t("Why:")} </span>
      <MathText text={text} />
    </div>
  );
}

type OptionState = "neutral" | "selected" | "correct" | "wrong";

/** A tappable option row used by the MCQ-family + true/false renderers. */
export function OptionRow({
  state,
  disabled,
  onClick,
  children,
}: {
  state: OptionState;
  disabled?: boolean;
  onClick?: () => void;
  children: ReactNode;
}) {
  const styles: Record<OptionState, string> = {
    neutral: "border-black/10 bg-white hover:border-brand-300 hover:bg-brand-50/50",
    selected: "border-brand-500 bg-brand-50 ring-1 ring-brand-300",
    correct: "border-emerald-400 bg-emerald-50 ring-1 ring-emerald-300",
    wrong: "border-red-400 bg-red-50 ring-1 ring-red-300",
  };
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cx(
        "flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm transition disabled:cursor-default",
        styles[state],
      )}
    >
      {children}
    </button>
  );
}
